// Copyright (C) 2022 Intel Corporation
// Copyright (C) 2022 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import Card from 'antd/lib/card';
import Descriptions from 'antd/lib/descriptions';
import Preview from 'components/common/preview';
import React from 'react';
import { useHistory } from 'react-router';
import { FileTextOutlined } from '@ant-design/icons';

interface Props {
    job: any;
}

function formatVideoDuration(startFrame: number, stopFrame: number): string {
    const frameCount = Math.max(0, stopFrame - startFrame + 1);
    return `${frameCount.toLocaleString()}`;
}

function JobCardComponent(props: Props): JSX.Element {
    const { job } = props;
    const history = useHistory();

    const openRoute = (event: React.MouseEvent, url: string): void => {
        if (event.ctrlKey || event.metaKey) {
            window.open(url, '_blank', 'noopener noreferrer');
        } else {
            history.push(url);
        }
    };

    const onOpenJob = (event: React.MouseEvent): void => {
        openRoute(event, `/tasks/${job.taskId}/jobs/${job.id}`);
    };

    const onOpenTask = (event: React.MouseEvent): void => {
        event.preventDefault();
        openRoute(event, `/tasks/${job.taskId}`);
    };
    const transcriptReview = ['phase4a', 'phase4b'].includes(job.phase);

    return (
        <Card
            className='cvat-job-page-list-item'
            cover={(
                <>
                    {transcriptReview ? (
                        <div
                            className='cvat-job-item-transcript-preview'
                            onClick={onOpenJob}
                            aria-label='Open transcript-only job'
                        >
                            <FileTextOutlined />
                            <span>Transcript only</span>
                        </div>
                    ) : (
                        <Preview
                            job={job}
                            onClick={onOpenJob}
                            loadingClassName='cvat-job-item-loading-preview'
                            emptyPreviewClassName='cvat-job-item-empty-preview'
                            previewWrapperClassName='cvat-jobs-page-job-item-card-preview-wrapper'
                            previewClassName='cvat-jobs-page-job-item-card-preview'
                        />
                    )}
                    <div className='cvat-job-page-list-item-id'>
                        ID:
                        {` ${job.id}`}
                    </div>
                </>
            )}
        >
            <Descriptions column={1} size='small'>
                <Descriptions.Item label='Task'>
                    {transcriptReview ? `#${job.taskId}` : (
                        <a href={`/tasks/${job.taskId}`} onClick={onOpenTask}>
                            {`#${job.taskId}`}
                        </a>
                    )}
                </Descriptions.Item>
                <Descriptions.Item label='Phase'>{job.phase}</Descriptions.Item>
                <Descriptions.Item label='Assignee'>{job.assignee?.username || 'Unassigned'}</Descriptions.Item>
                <Descriptions.Item label='State'>
                    {job.state === 'in progress' ? `In progress (${job.progress || 0}%)` :
                     job.state === 'completed' && job.phase === 'phase3b' ? 'Submitted' :
                     job.state.charAt(0).toUpperCase() + job.state.slice(1)}
                </Descriptions.Item>
                {transcriptReview ? (
                    <Descriptions.Item label='Content'>Transcript only</Descriptions.Item>
                ) : (
                    <Descriptions.Item label='# Frames'>
                        {formatVideoDuration(job.startFrame, job.stopFrame)}
                    </Descriptions.Item>
                )}
            </Descriptions>
        </Card>
    );
}

export default React.memo(JobCardComponent);
